/* =========================================================
   BTB COLLECTION AJAX
   Built: September 4, 2026

   PURPOSE
   ---------------------------------------------------------
   Progressive enhancement for BTB's URL-driven collection
   Sort + Filter controls.

   The real Shopify URLs remain the source of truth.

   Without JavaScript:
   - Sort works
   - Filters work
   - Full page navigation occurs

   With this JavaScript:
   - The destination URL is fetched in the background
   - Only the collection area is replaced
   - Browser URL updates normally
   - Back / forward navigation still works

   IMPORTANT:
   Do not remove the real href values from filter/sort links.
   They are the fallback if AJAX fails.
   ========================================================= */

(function () {
  if (window.BTBCollectionAjaxInitialized) return;

  window.BTBCollectionAjaxInitialized = true;

  const ROOT_SELECTOR = '[data-btb-collection-root]';

  let activeController = null;


  /* ---------------------------------------------------------
     HELPERS
     --------------------------------------------------------- */

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }


  function getDetailsLabel(details) {
    const label = details.querySelector(
      '.btb-filter-group__heading > span:first-child'
    );

    return label ? label.textContent.trim() : '';
  }


  function captureOpenState(root) {
    let openControl = null;

    if (root.querySelector('.btb-inline-filter-shell[open]')) {
      openControl = 'filter';
    }

    if (root.querySelector('.btb-inline-sort-shell[open]')) {
      openControl = 'sort';
    }


    const openGroups = Array.from(
      root.querySelectorAll('.btb-filter-group[open]')
    )
      .map(getDetailsLabel)
      .filter(Boolean);


    return {
      openControl,
      openGroups
    };
  }


  function restoreOpenState(root, state, preserveControl) {
    if (!state) return;


    /* Keep Filter open while somebody is actively filtering */
    if (preserveControl && state.openControl === 'filter') {
      const filterShell = root.querySelector(
        '.btb-inline-filter-shell'
      );

      if (filterShell) {
        filterShell.open = true;
      }
    }


    /* Restore open accordion groups */
    if (preserveControl && state.openGroups.length) {
      const groups = Array.from(
        root.querySelectorAll('.btb-filter-group')
      );


      state.openGroups.forEach(function (wantedLabel) {
        const matchingGroup = groups.find(function (group) {
          return getDetailsLabel(group) === wantedLabel;
        });


        if (matchingGroup) {
          matchingGroup.open = true;
        }
      });
    }
  }


  function setLoading(root, loading) {
    if (!root) return;


    if (loading) {
      root.classList.add('btb-collection-is-loading');
      root.setAttribute('aria-busy', 'true');
    } else {
      root.classList.remove('btb-collection-is-loading');
      root.removeAttribute('aria-busy');
    }
  }


  /* ---------------------------------------------------------
     MAIN COLLECTION UPDATE
     --------------------------------------------------------- */

  async function updateCollection(
    destination,
    options = {}
  ) {
    const root = getRoot();


    if (!root) {
      window.location.href = destination;
      return;
    }


    const preserveControl =
      options.preserveControl === true;

    const updateHistory =
      options.updateHistory !== false;


    const openState = captureOpenState(root);

    const scrollPosition = window.scrollY;


    /* Cancel an unfinished previous collection request */
    if (activeController) {
      activeController.abort();
    }


    const controller = new AbortController();

    activeController = controller;


    setLoading(root, true);


    try {
      const response = await fetch(destination, {
        method: 'GET',

        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },

        signal: controller.signal
      });


      if (!response.ok) {
        throw new Error(
          'BTB collection request failed: ' +
          response.status
        );
      }


      const html = await response.text();

      const parser = new DOMParser();

      const nextDocument = parser.parseFromString(
        html,
        'text/html'
      );


      const nextRoot = nextDocument.querySelector(
        ROOT_SELECTOR
      );


      if (!nextRoot) {
        throw new Error(
          'BTB collection root was not found in response.'
        );
      }


      /*
        Replace ONLY the inside of the collection area.

        Header, navigation, collection title, etc. remain
        untouched.
      */
      root.innerHTML = nextRoot.innerHTML;


      /* Update browser URL without refreshing */
      if (updateHistory) {
        window.history.pushState(
          {
            btbCollectionAjax: true
          },
          '',
          destination
        );
      }


      restoreOpenState(
        root,
        openState,
        preserveControl
      );


      /*
        Prevent the browser from suddenly jumping somewhere
        simply because product heights changed.
      */
      window.scrollTo({
        top: scrollPosition,
        behavior: 'instant'
      });


      document.dispatchEvent(
        new CustomEvent('btb:collection:updated', {
          detail: {
            url: destination
          }
        })
      );

    } catch (error) {

      /*
        Aborted request = another newer request already started.
        Do nothing.
      */
      if (error.name === 'AbortError') {
        return;
      }


      console.error(error);


      /*
        Progressive enhancement fallback.

        If our AJAX code ever breaks, the same Shopify URL
        still works normally.
      */
      window.location.href = destination;

    } finally {

      /*
        Only remove loading state if this is still the newest
        request.
      */
      if (activeController === controller) {
        activeController = null;

        setLoading(
          getRoot(),
          false
        );
      }
    }
  }


  /* ---------------------------------------------------------
     FILTER + SORT LINKS
     --------------------------------------------------------- */

  document.addEventListener('click', function (event) {

    /*
      Leave modified clicks alone so:
      Command-click / Ctrl-click / new tabs still work.
    */
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }


    const link = event.target.closest(
      [
        '[data-btb-filter-link]',
        '.btb-sort-item__content',
        '.collection__top-row a'
      ].join(',')
    );


    if (!link) return;


    const root = link.closest(ROOT_SELECTOR);

    if (!root) return;


    if (
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return;
    }


    const url = new URL(
      link.href,
      window.location.href
    );


    if (
      url.origin !== window.location.origin
    ) {
      return;
    }


    event.preventDefault();


    /*
      Keep Filter open after selecting/removing filter values
      so a shopper can choose M, then Blue, then Dresses
      without reopening Filter every time.

      Sort closes after making a choice.
    */
    const isSortLink =
      link.matches('.btb-sort-item__content');


    updateCollection(
      url.href,
      {
        preserveControl: !isSortLink,
        updateHistory: true
      }
    );
  });


  /* ---------------------------------------------------------
     PRICE FORM
     --------------------------------------------------------- */

  document.addEventListener('submit', function (event) {
    const form = event.target.closest(
      '.btb-filter-price'
    );


    if (!form) return;


    const root = form.closest(ROOT_SELECTOR);

    if (!root) return;


    event.preventDefault();


    const url = new URL(
      form.action || window.location.pathname,
      window.location.origin
    );


    const formData = new FormData(form);


    for (const [key, value] of formData.entries()) {
      const cleanedValue = String(value).trim();


      /*
        Don't add blank min/max prices to the URL.
      */
      if (cleanedValue !== '') {
        url.searchParams.append(
          key,
          cleanedValue
        );
      }
    }


    updateCollection(
      url.href,
      {
        preserveControl: true,
        updateHistory: true
      }
    );
  });


  /* ---------------------------------------------------------
     BROWSER BACK / FORWARD
     --------------------------------------------------------- */

  window.addEventListener('popstate', function () {
    updateCollection(
      window.location.href,
      {
        preserveControl: false,
        updateHistory: false
      }
    );
  });

})();